import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { forkJoin } from 'rxjs';
import { RestaurantTable, RestaurantTablesService } from '../restaurant-tables.service';
import { DiningArea, DiningAreasService } from '../../dining-areas/dining-areas.service';
import { TableFormDialogComponent } from '../table-form-dialog/table-form-dialog.component';
import { HasPermissionDirective } from '../../../../../core/directives/has-permission.directive';
import { ConfirmationService } from '../../../../../shared/services/confirmation.service';
import { PageHeaderService } from '../../../../../shared/services/page-header.service';
import { SharedModule } from '../../../../../shared/shared.module';

interface AreaSection {
  area: DiningArea;
  tables: RestaurantTable[];
}

@Component({
  selector: 'app-tables-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    HasPermissionDirective,
    SharedModule,
  ],
  template: `
    <section class="config-page">
      <div *ngIf="loading" class="loading-container">
        <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
        <p>Loading tables...</p>
      </div>

      <div *ngIf="!loading && sections.length === 0" class="empty-state">
        <div class="empty-state__icon"><mat-icon>table_restaurant</mat-icon></div>
        <h3>No dining areas yet</h3>
        <p>Create a dining area first, then add tables to it.</p>
        <a mat-flat-button color="primary" routerLink="/config/dining-areas">
          <mat-icon>add</mat-icon> Go to Dining Areas
        </a>
      </div>

      <app-card
        *ngIf="!loading && sections.length > 0"
        variant="default"
        padding="none"
        class="area-section"
      >
      <div class="area-toolbar">
        <div class="area-switch" role="tablist">
          <button
            type="button"
            class="area-switch__btn"
            *ngFor="let section of sections"
            role="tab"
            [class.is-active]="section.area.id === activeAreaId"
            [attr.aria-selected]="section.area.id === activeAreaId"
            [matTooltip]="section.area.name"
            (click)="selectArea(section.area.id)"
          >
            <mat-icon class="tab-icon">table_restaurant</mat-icon>
            <span class="tab-name">{{ section.area.name }}</span>
            <span class="count-chip">{{ section.tables.length }}</span>
          </button>
        </div>
      </div>

      <ng-container *ngIf="activeSection as section">
          <div class="tab-body">
            <div class="tab-toolbar">
              <span class="area-summary" *ngIf="section.tables.length > 0">
                <mat-icon>table_restaurant</mat-icon>
                {{ section.tables.length }} {{ section.tables.length === 1 ? 'table' : 'tables' }}
                <span class="dot">&middot;</span>
                {{ areaSeatTotal(section) }} seats
              </span>
              <span class="tab-toolbar__spacer"></span>
              <button
                mat-stroked-button
                color="primary"
                *appHasPermission="'table.create'"
                (click)="onCreate(section.area.id)"
              >
                <mat-icon>add</mat-icon> Add Table
              </button>
            </div>

            <div *ngIf="section.tables.length === 0" class="section-empty">
              No tables in this area yet.
            </div>

            <div *ngIf="section.tables.length > 0" class="tables-grid">
          <div class="table-tile" *ngFor="let t of section.tables" [class.is-inactive]="!t.is_active">
            <div class="table-tile__top">
              <button
                mat-icon-button
                [matMenuTriggerFor]="menu"
                matTooltip="More actions"
                class="action-menu-trigger"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #menu="matMenu" class="action-menu">
                <button mat-menu-item (click)="onEdit(t)" *appHasPermission="'table.update'">
                  <mat-icon>edit</mat-icon><span>Edit</span>
                </button>
                <button mat-menu-item (click)="onToggle(t)" *appHasPermission="'table.update'">
                  <mat-icon>{{ t.is_active ? 'toggle_off' : 'toggle_on' }}</mat-icon>
                  <span>{{ t.is_active ? 'Deactivate' : 'Activate' }}</span>
                </button>
                <mat-divider></mat-divider>
                <button mat-menu-item (click)="onDelete(t)" class="danger-action" *appHasPermission="'table.delete'">
                  <mat-icon>delete</mat-icon><span>Delete</span>
                </button>
              </mat-menu>
            </div>

            <div class="table-tile__graphic">
              <svg viewBox="0 0 120 100" class="table-svg" [style.color]="tableColor">
                <ng-container *ngFor="let x of tableChairs(t.capacity).top">
                  <rect [attr.x]="x - 7" y="16" width="14" height="10" rx="3" class="chair"></rect>
                </ng-container>
                <ng-container *ngFor="let x of tableChairs(t.capacity).bottom">
                  <rect [attr.x]="x - 7" y="74" width="14" height="10" rx="3" class="chair"></rect>
                </ng-container>
                <rect x="30" y="34" width="60" height="32" rx="9" class="table-top"></rect>
                <text x="60" y="54" text-anchor="middle" dominant-baseline="middle" class="table-num">
                  {{ t.table_number }}
                </text>
              </svg>
            </div>

            <div class="table-tile__meta">
              <span class="seats"><mat-icon>event_seat</mat-icon>{{ t.capacity }} seats</span>
              <span class="status-badge" [class.active]="t.is_active" [class.inactive]="!t.is_active">
                <span class="status-indicator"></span>{{ t.is_active ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </div>
            </div>
          </div>
      </ng-container>
      </app-card>
    </section>
  `,
  styleUrls: ['../../config-list.shared.scss'],
  styles: [`
    .area-section {
      border-radius: 16px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      border: 1px solid rgba(226, 232, 240, 0.8);
      overflow: hidden;
    }
    .area-toolbar {
      display: flex;
      padding: 1rem 1.5rem;
      border-bottom: 1px solid #e2e8f0;
      background: #f8fafc;
    }
    /* .area-switch chip styles live in config-list.shared.scss so they can
       use the brand theme palette ($primary-*). */
    .tab-body { padding: 0.5rem 0; }
    .tab-toolbar {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem 1.5rem 0;
    }
    .tab-toolbar__spacer { flex: 1 1 auto; }
    .area-summary {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      color: #475569;
      font-size: 0.85rem;
      font-weight: 500;

      mat-icon { font-size: 18px; width: 18px; height: 18px; color: #94a3b8; }
      .dot { color: #cbd5e1; }
    }
    .section-empty {
      padding: 1.5rem;
      color: #94a3b8;
      font-size: 0.9rem;
    }
    .tables-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
      gap: 1.25rem;
      padding: 1.25rem 1.5rem;
    }
    .table-tile {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.875rem 1rem 1rem;
      border: 1px solid #e2e8f0;
      border-radius: 14px;
      background: #fff;
      transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

      &:hover {
        border-color: #c7d2fe;
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
        transform: translateY(-2px);
      }
      &.is-inactive { opacity: 0.6; }
    }
    .table-tile__top {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      min-height: 36px;
    }
    .table-tile__graphic {
      display: flex;
      justify-content: center;
      padding: 0.25rem 0;
    }
    .table-svg {
      width: 130px;
      height: 108px;

      .chair {
        fill: color-mix(in srgb, currentColor 14%, #fff);
        stroke: currentColor;
        stroke-width: 1.5;
      }
      .table-top {
        fill: color-mix(in srgb, currentColor 8%, #fff);
        stroke: currentColor;
        stroke-width: 2;
      }
      .table-num {
        fill: #0f172a;
        font-size: 15px;
        font-weight: 700;
      }
    }
    .table-tile__meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;

      .seats {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        color: #475569;
        font-size: 0.85rem;
        font-weight: 500;

        mat-icon { font-size: 17px; width: 17px; height: 17px; color: #94a3b8; }
      }
    }
    .table-tile .status-badge {
      font-size: 0.72rem;
      padding: 0.2rem 0.6rem;
    }
  `],
})
export class TablesListComponent implements OnInit, OnDestroy {
  sections: AreaSection[] = [];
  activeAreaId: string | null = null;
  loading = false;

  get activeSection(): AreaSection | undefined {
    return this.sections.find((s) => s.area.id === this.activeAreaId) ?? this.sections[0];
  }

  selectArea(areaId: string): void {
    this.activeAreaId = areaId;
  }

  constructor(
    private service: RestaurantTablesService,
    private areasService: DiningAreasService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private pageHeader: PageHeaderService,
  ) {}

  ngOnInit(): void {
    this.pageHeader.set({
      title: 'Tables',
      icon: 'table_bar',
      backRoute: '/dashboard',
      actions: [
        { label: 'Refresh', icon: 'refresh', variant: 'stroked', permission: 'table.read', disabled: () => this.loading, handler: () => this.load() },
      ],
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeader.clear();
  }

  load(): void {
    this.loading = true;
    forkJoin({
      areas: this.areasService.getAll(),
      tables: this.service.getAll(),
    }).subscribe({
      next: ({ areas, tables }) => {
        const areaList = (areas.success && areas.data) ? areas.data : [];
        const tableList = (tables.success && tables.data) ? tables.data : [];
        this.sections = areaList.map((area) => ({
          area,
          tables: tableList.filter((t) => t.dining_area_id === area.id),
        }));
        if (!this.sections.some((s) => s.area.id === this.activeAreaId)) {
          this.activeAreaId = this.sections[0]?.area.id ?? null;
        }
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load tables', 'Close', { duration: 3000 });
        this.loading = false;
      },
    });
  }

  readonly tableColor = '#10b981';

  areaSeatTotal(section: AreaSection): number {
    return section.tables.reduce((sum, t) => sum + (t.capacity || 0), 0);
  }

  tableChairs(capacity: number): { top: number[]; bottom: number[] } {
    const cap = Math.max(0, Math.min(Math.floor(capacity || 0), 12));
    const topCount = Math.ceil(cap / 2);
    const bottomCount = cap - topCount;
    const spread = (n: number): number[] => {
      if (n <= 0) return [];
      const left = 30, right = 90;
      if (n === 1) return [(left + right) / 2];
      const step = (right - left) / (n - 1);
      return Array.from({ length: n }, (_, i) => left + step * i);
    };
    return { top: spread(topCount), bottom: spread(bottomCount) };
  }

  onCreate(areaId?: string): void {
    this.dialog.open(TableFormDialogComponent, { width: '560px', maxHeight: '90vh', data: { diningAreaId: areaId } })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onEdit(t: RestaurantTable): void {
    this.dialog.open(TableFormDialogComponent, { width: '560px', maxHeight: '90vh', data: { id: t.id } })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onToggle(t: RestaurantTable): void {
    this.service.toggleActive(t.id).subscribe({
      next: (res) => {
        if (res.success) {
          t.is_active = !t.is_active;
          this.snackBar.open(`Table ${t.is_active ? 'activated' : 'deactivated'}`, 'Close', { duration: 3000 });
        }
      },
      error: () => this.snackBar.open('Failed to update status', 'Close', { duration: 3000 }),
    });
  }

  onDelete(t: RestaurantTable): void {
    this.confirmationService.confirm({
      title: 'Delete table',
      message: `Are you sure you want to delete table "${t.table_number}"?`,
      confirmText: 'Delete',
      type: 'danger',
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.service.delete(t.id).subscribe({
        next: (res) => {
          if (res.success) {
            this.snackBar.open('Table deleted', 'Close', { duration: 3000 });
            this.load();
          }
        },
        error: (error) => this.snackBar.open(error.error?.message || 'Failed to delete', 'Close', { duration: 5000 }),
      });
    });
  }
}
